/**
 * Website-wide player designs (since 0.15.45).
 *
 * A player design is a visual layer on top of each journey's own theme. It never
 * changes journeys, media or published releases. WordPress decides the active
 * design (option `lgsiv_player_design`, per-context overrides and the filter
 * `lgsiv_player_design`) and passes it to the web component as the attribute
 * `player-design`. Unknown or missing values always fall back to `standard`, so an
 * existing installation looks exactly as before until a design is chosen.
 */
export type PlayerDesign = 'standard' | 'liquid-glass';

/**
 * Seit 0.15.46 (Marcus, 29.10.2026): Liquid Glass ist das einzige FrontFace-Aussehen. Der alte Look entfällt;
 * jeder Wert – auch fehlend oder „standard“ – ergibt Liquid Glass. Farben/Texte bleiben im Journey-Editor einstellbar.
 */
export const PLAYER_DESIGNS: Partial<Record<PlayerDesign, { label: string; description: string }>> = {
  'liquid-glass': {
    label: 'Liquid Glass',
    description: 'Glasflächen mit Glanzkante, Navy/Magenta/Cyan, Kinoansicht für 16:9-Aufnahmen und dezente Federbewegungen.',
  },
};

export function resolvePlayerDesign(_value?: string | null): PlayerDesign {
  return 'liquid-glass';
}

/**
 * Design tokens appended after the journey theme variables. Later declarations in the
 * same inline style win, so the journey theme stays untouched in its own data.
 */
export function playerDesignVars(design: PlayerDesign): string {
  if (design !== 'liquid-glass') return '';
  // Leading separator: the journey variables end without a semicolon.
  return ';' + [
    '--accent:#57d4ef',
    '--accent-2:#d7195f',
    '--button-hover:#57d4ef',
    '--bg:#071624',
    '--surface:#0c2234',
    '--text:#f5f8fb',
    '--muted:#a8b8c6',
    '--radius:26px',
    '--heading-font:Tablet,Inter,ui-sans-serif,system-ui,sans-serif',
    '--body-font:Tablet,Inter,ui-sans-serif,system-ui,sans-serif',
    '--button-font:Tablet,Inter,ui-sans-serif,system-ui,sans-serif',
    '--font:Tablet,Inter,ui-sans-serif,system-ui,sans-serif',
    '--glow-blur:0px',
  ].join(';') + ';';
}

/** Critically damped spring for repositioning (damping 1, response ~0.36 s). */
export const SPRING_SETTLE = 'linear(0,0.0859,0.2542,0.4292,0.5808,0.701,0.7913,0.8568,0.9031,0.9352,0.9571,0.9719,0.9817,0.9881,0.9924,0.9951,0.9969,0.998,1)';
/** Subtle spring with ~1 % overshoot (damping 0.82, response 0.42 s) for opening surfaces. */
export const SPRING_SOFT = 'linear(0,0.0523,0.1718,0.3173,0.4635,0.596,0.7084,0.7988,0.8683,0.9196,0.9559,0.9802,0.9957,1.0048,1.0093,1.011,1.0109,1.0098,1.0082,1.0066,1.005,1.0037,1.0026,1.0017,1.0011,1.0006,1)';

interface LiteEnvironment {
  rootClassList?: { contains(token: string): boolean } | null;
  rootHasAttribute?: (name: string) => boolean;
  saveData?: boolean;
}

/**
 * Energy-saving mode: the website's own lite switch (`html.lq-lite` from Liquid or the
 * neutral `html[data-frontface-lite]`) or the browser's data saver. The player then
 * drops live blur and decorative motion but keeps every control usable.
 */
export function isLiteEnvironment(env: LiteEnvironment): boolean {
  if (env.saveData) return true;
  if (env.rootClassList?.contains('lq-lite')) return true;
  return Boolean(env.rootHasAttribute?.('data-frontface-lite'));
}

